import Icon from "@mdi/react";
import { mdiPlus, mdiVote } from "@mdi/js";
import { Link } from "easy-page-router/react";

import { Footer, Header } from "../../Layout";
import { useHead } from "../../services/common";
import { ContentSwitch, Loading, OtherPolls, Page, VoteResult } from "../../components";
import { ThemeDiv, ThemeSwitch } from "../../components/Theme";
import { LangSwitch } from "../../components/Lang";
import { useLang } from "../../services/lang";
import { useLocalHistory } from "../../services/localStorage";
import { useApi, useHandleError } from "../../services/api";
import { PollPreview } from "../../../types";
import { useSSRHook } from "ssr-hook";
import { useEffect, useState } from "react";
import LaunchButton from "../../components/LaunchButton";


export default function PollBoard() {
    const { dict } = useLang();

    useHead({
        title: dict.seo.pollBoard.title,
        description: dict.seo.pollBoard.description,
    });

    const [contentIndex, setContentIndex] = useState(1);

    return <Page fixed={<>
        <Header title={dict.what} link={`/${dict.urlPoll}`}>
            <LangSwitch />
            <ThemeSwitch />
        </Header>
        <Footer />
    </>}>

        <h1>{dict.what} <span className="sub-title">{dict.whatSubTitle}</span></h1>

        <p className="page-description">{dict.seo.pollBoard.text}</p>

        <Link className="button-new" href={`/${dict.urlPoll}/${dict.urlNew}`}>
            <Icon path={mdiPlus} size={1} /> {dict.newPoll} <Icon path={mdiVote} size={1} />
        </Link>

        <OtherPolls who when />

        <ContentSwitch
            index={contentIndex}
            onChange={setContentIndex}
            options={[{
                title: dict.pollsLocal,
                content: <Local onLoad={() => setContentIndex(0)} />,
            }, {
                title: dict.pollsPublic,
                content: <Public />,
            }]}
        />

    </Page>;
}


function Local({ onLoad }: { onLoad: () => void }) {
    const pollIds = useLocalHistory("poll");
    const [polls, error, isLoading, reload] = useApi.getPollPreviews({ pollIds });
    useHandleError(error);

    useEffect(() => {
        if (polls && polls.length > 0) onLoad();
    }, [polls]);

    return <Show polls={polls} isLoading={isLoading} error={error} />;
}

function Public() {
    const { lang } = useLang();

    const [polls, error, isLoading, reload] = useSSRHook<PollPreview[]>(`/api/poll-previews-public?lang=${lang}`);
    useHandleError(error);

    return <Show polls={polls} isLoading={isLoading} error={error} />;
}

type ShowProps = {
    polls: null | PollPreview[];
    error: null | string | Error;
    isLoading: boolean;
}

function Show({ polls, isLoading, error }: ShowProps) {
    const { dict } = useLang();

    return <>
        {isLoading && <Loading />}
        {error && <p className="error">{dict.pollsError}</p>}
        {polls?.length === 0 && <p className="info">{dict.noPolls}</p>}
        {polls && <div className="grid polls">
            {polls.map(p => <PollPreviewElement key={p._id} poll={p} className="grid-item" />)}
        </div>}
    </>
}


function PollPreviewElement({ poll, className }: { poll: PollPreview, className: string }) {
    const { dict } = useLang();

    const maxVoteCount = poll.options[0]?.voteCount || 0;

    return <Link href={`/${dict.urlPoll}/${poll._id}`}>
        <ThemeDiv theme={poll.theme} className={`poll-preview preview theme-${poll.theme} ${className}`}>
            <h3 className="line-clamp-2">{poll.title}</h3>
            <p className="pre-line line-clamp-3">{poll.description}</p>
            <ul className="poll-options">
                {poll.options.filter((_, i) => i < 3).map(o => <li key={o.title} className={`poll-option order-${o.order}`}>
                    <VoteResult
                        title={`${o.voteCount}x ${o.title}`}
                        yes={o.voteCount}
                        maybe={maxVoteCount - o.voteCount}
                        no={0}
                    />
                </li>)}
                {poll.options.length > 3 && <li style={{ textAlign: "center" }}>...</li>}
            </ul>
        </ThemeDiv>
    </Link>;
}